import { useEffect, useState } from "react";
import { FiActivity } from "react-icons/fi";
import bankService from "../../../services/bank.service";
import PaginationBar from "../../../components/common/PaginationBar";
import { PAGE_LIMIT } from "../../../constants/pagination";
import "../../../styles/users.css";
import "../../../styles/ssdiLinkBanker.css";

function dash(value) {
  const text = String(value ?? "").trim();
  return text || "—";
}

function formatLinkedOn(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

function formatRegistrationDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB");
}

function serialNumber(index, total) {
  return Math.max(Number(total) - index, 1);
}

function statusClass(status) {
  const key = String(status || "").toLowerCase();
  if (key === "active" || key === "pending") return key;
  return "inactive";
}

function AgencyUsers({ agency }) {
  return (
    <div className="sla-users">
      <strong>{Number(agency.totalUsers) || 0}</strong>
      <small>
        Active {Number(agency.activeUsers) || 0} · Inactive {Number(agency.inactiveUsers) || 0}
      </small>
    </div>
  );
}

export default function BankTracing() {
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setLoading(true);
    bankService
      .listLinkedAgencies()
      .then((res) => {
        setAgencies(res?.data?.data?.linkedAgencies || []);
      })
      .catch(() => setAgencies([]))
      .finally(() => setLoading(false));
  }, []);

  const total = agencies.length;
  const totalPages = total ? Math.ceil(total / PAGE_LIMIT) : 0;
  const safePage = Math.min(page, totalPages || 1);
  const start = (safePage - 1) * PAGE_LIMIT;
  const pageRows = agencies.slice(start, start + PAGE_LIMIT);

  return (
    <div className="page">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
        <FiActivity size={22} />
        <h2>Linked Agencies — Who is using my data?</h2>
      </div>

      {loading ? (
        <p className="muted">Loading...</p>
      ) : agencies.length === 0 ? (
        <div style={{ padding: 32, textAlign: "center", color: "var(--muted,#6b7280)" }}>
          <FiActivity size={40} style={{ opacity: 0.3 }} />
          <p style={{ marginTop: 12 }}>No agencies linked to your data yet.</p>
          <p style={{ fontSize: 13 }}>SSDI will connect agencies to your account.</p>
        </div>
      ) : (
        <div className="company-table-wrap ssdi-excel-sheet table-scroll sla-table-wrap">
          <table className="users-table excel-grid-table sla-table">
            <thead>
              <tr>
                <th>S.No.</th>
                <th>Agency Code</th>
                <th>Agency Name</th>
                <th>Admin Name</th>
                <th>First Confirmation Number</th>
                <th>Second Confirmation Number</th>
                <th>Admin Email</th>
                <th>Repo Agency Email</th>
                <th>PAN Number</th>
                <th>GST Number</th>
                <th>Aadhaar Number</th>
                <th>Address</th>
                <th>Pincode</th>
                <th>Registration Date</th>
                <th>Agency Status</th>
                <th>Users</th>
                <th>Linked By Bank / NBFC Employee Name</th>
                <th>Linked By Employee Number</th>
                <th>Linked By Email</th>
                <th>Linked By Mobile</th>
                <th>Linked On</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((agency, idx) => (
                <tr key={agency.linkId || agency.id || idx}>
                  <td>{serialNumber(start + idx, total)}</td>
                  <td>{dash(agency.code)}</td>
                  <td>{dash(agency.name)}</td>
                  <td>{dash(agency.adminName)}</td>
                  <td className="sla-nowrap">{dash(agency.firstConfirmationNumber)}</td>
                  <td className="sla-nowrap">{dash(agency.secondConfirmationNumber)}</td>
                  <td>{dash(agency.adminEmail)}</td>
                  <td>{dash(agency.companyEmail)}</td>
                  <td className="sla-nowrap">{dash(agency.panNumber)}</td>
                  <td className="sla-nowrap">{dash(agency.gstNumber)}</td>
                  <td className="sla-nowrap">{dash(agency.aadhaarNumber)}</td>
                  <td className="sla-address-td">
                    <span className="sla-address" title={agency.address || ""}>
                      {dash(agency.address)}
                    </span>
                  </td>
                  <td>{dash(agency.pincode)}</td>
                  <td className="sla-nowrap">{formatRegistrationDate(agency.registrationDate)}</td>
                  <td>
                    <span className={`company-status company-status--${statusClass(agency.status)}`}>
                      {dash(agency.status)}
                    </span>
                  </td>
                  <td>
                    <AgencyUsers agency={agency} />
                  </td>
                  <td>{dash(agency.linkedBy?.name)}</td>
                  <td className="sla-nowrap">{dash(agency.linkedBy?.employeeNumber)}</td>
                  <td>{dash(agency.linkedBy?.email)}</td>
                  <td className="sla-nowrap">{dash(agency.linkedBy?.mobile)}</td>
                  <td className="sla-nowrap">{formatLinkedOn(agency.linkedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <PaginationBar
        page={safePage}
        totalPages={totalPages}
        total={total}
        loading={loading}
        onPageChange={setPage}
      />
    </div>
  );
}
